<template>
  <div class="animated fadeIn">
    <div class="row">

      <div class="col-xl">
        <b-card>
          <template slot="header">
            <div class="form-row">
              <div class="form-group col-md">
                  <treeselect ref="structureTreeselect"
                              v-model="surveyValue"
                              :auto-load-root-options="false"
                              :placeholder="$t('Webportal.SurveyFilter.Placeholder')"
                              :options="structureItems"
                              :disabled="isExporting"
                              :disableFuzzyMatching="true"
                              :showCount="true"
                              showCountOf="LEAF_DESCENDANTS"
                              :disable-branch-nodes="true"
                              @input="surveyFilterInput">
                  </treeselect>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md">
                  <treeselect ref="waveTreeselect"
                              v-model="waveValue"
                              :auto-load-root-options="false"
                              :placeholder="$t('Webportal.WaveFilter.Placeholder')"
                              :options="waveItems"
                              :disabled="noSurveySelected || isExporting"
                              :disableFuzzyMatching="true"
                              :multiple="true"
                              :showCount="true"
                              showCountOf="LEAF_DESCENDANTS"
                              :flat="true"
                              :defaultExpandLevel="1"
                              :disable-branch-nodes="true">
                  </treeselect>
              </div>
              <div class="form-group col-md">
                  <treeselect ref="scenarioTreeselect"
                              v-model="scenarioValue"
                              :auto-load-root-options="false"
                              :placeholder="$t('Webportal.ScenarioFilter.Placeholder')"
                              :options="scenarioItems"
                              :disabled="noSurveySelected || isExporting"
                              :disableFuzzyMatching="true"
                              :multiple="true"
                              :showCount="true"
                              showCountOf="LEAF_DESCENDANTS"
                              :flat="true"
                              :defaultExpandLevel="1"
                              :disable-branch-nodes="true">
                  </treeselect>
              </div>
              <div class="form-group col-md">
                <treeselect ref="propertyTreeselect"
                            v-model="propertyValue"
                            :auto-load-root-options="false"
                            :placeholder="$t('Webportal.PropertyValueFilter.Placeholder')"
                            :options="propertyItems"
                            :disabled="noSurveySelected || isExporting"
                            :disableFuzzyMatching="true"
                            :multiple="true"
                            :showCount="true"
                            showCountOf="LEAF_DESCENDANTS"
                            :flat="true"
                            :defaultExpandLevel="1"
                            :disable-branch-nodes="true">
                </treeselect>
              </div>
            </div>
          </template>

          <b-form-group :label-cols-sm="2" :label="$t('Webportal.Subtotals')" label-class="font-weight-bold">
            <treeselect ref="propertySubtotalTreeselect"
                        v-model="propertySubtotalValue"
                        :auto-load-root-options="false"
                        :placeholder="$t('Webportal.PropertyFilter.Placeholder')"
                        :options="propertySubtotalItems"
                        :disabled="noSurveySelected || isExporting"
                        :disableFuzzyMatching="true"
                        :multiple="false"
                        :showCount="true"
                        showCountOf="LEAF_DESCENDANTS"
                        :defaultExpandLevel="1"
                        :disable-branch-nodes="true"
                        style="max-width:20em">
            </treeselect>
          </b-form-group>

          <b-form-group v-if="isMSMUser"
                        :label-cols-sm="2" :label="$t('Webportal.Status')" label-class="font-weight-bold"
                        :disabled="isExporting">
            <b-form-group>
              <div class="form-row">
                <div class="col-auto">
                  <b-form-input v-model="statusMinValue"
                                type="number"
                                min="0"
                                max="400"
                                style="max-width:5em"></b-form-input>
                </div>
                <div class="col-auto">
                  <label class="col-form-label">{{ $t('Webportal.RangeTo') }}</label>
                </div>
                <div class="col-auto">
                  <b-form-input v-model="statusMaxValue"
                                type="number"
                                min="0"
                                max="400"
                                style="max-width:5em"></b-form-input>
                </div>
              </div>
            </b-form-group>
            <b-form-group>
              <b-form-checkbox v-model="includeTestchecks"
                              :disabled="noSurveySelected || isExporting">
                              {{ $t('Webportal.Include') }} {{ $t('Webportal.IncludeTestchecks') }}</b-form-checkbox>
            </b-form-group>
          </b-form-group>

          <div class="form-row">
            <div class="col-md-auto">
              <b-alert :show="noDataAlert"
                      dismissible
                      @dismissed="noDataAlert=0"
                      variant="warning">
                {{ $t('Webportal.NoDataFound') }}
              </b-alert>
              <b-button variant="primary"
                        :disabled="noSurveySelected || isExporting"
                        @click="downloadFrequencies()">
                {{ $t('Webportal.Download') }}
                <font-awesome-icon icon="download" />
              </b-button>
            </div>
            <div class="col-md-1" v-bind:class="{ inlineSpinner: isExporting }"></div>
          </div>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import DownloadFileMixin from '@/mixins/DownloadFileMixin'
import EventBus from '@/services/EventBus.js';
import SurveysService from '@/services/SurveysService'
import ReportsService from '@/services/ReportsService'
import AuthenticationService from '@/services/AuthenticationService'

export default {
  name: "Frequencies",

  mixins: [ DownloadFileMixin ],

  data () {
    return {
      // Filter properties
      structureItems: [],                   // Data items for structure treeselect
      surveyValue: null,                    // Selected structure items
      waveItems: [],                        // Data items for wave treeselect
      waveValue: [],                        // Selected wave items
      scenarioItems: [],                    // Data items for scenarios
      scenarioValue: [],                    // Selected scenarios
      propertyItems: [],                    // Data items for property treeselect
      propertyValue: [],                    // Selected property items
      propertySubtotalItems: [],            // Data items for property subtotal treeselect
      propertySubtotalValue: null,          // Selected property subtotal items
      statusMinValue: 220,                  // Customer Min/Max Values
      statusMaxValue: 220,
      includeTestchecks: false,

      isExporting: false,
      noDataAlert: 0
    }
  },

  computed: {

    isMSMUser: function () {
      return AuthenticationService.isMSMUser;
    },

    noSurveySelected: function () {
      return _.isEmpty(this.surveyValue);
    },

    getMinimumStatus: function() {
      if (!this.isMSMUser) {
        return null
      }

      return this.statusMinValue
    },

    getMaximumStatus: function() {
     if (!this.isMSMUser) {
        return null
      }

      return this.statusMaxValue
    },

    filter: function() {
      let result = {
        surveyIds: [],
        waveIds: this.waveValue,
        scenarioIds: this.scenarioValue,
        propertyIds: this.propertyValue,
        surveyInstanceStatusMinimum: this.getMinimumStatus,
        surveyInstanceStatusMaximum: this.getMaximumStatus,
        includeTestchecks: this.includeTestchecks
      };

      if (this.surveyValue)
      {
        let id = this.surveyValue.substring(1);
        result.surveyIds.push(id);
      }

      return result;
    },

    options: function() {
      let result = {
        aggregationLevel: this.propertySubtotalValue
      }

      return result;
    },

    defaultSubtotalItems: function()  {
      return [ { id: 'Wave', label: this.$t('Webportal.Wave') },
               { id: 'Scenario', label: this.$t('Webportal.Scenario') } ];
    },
  },

  mounted() {
    this.getSurveyStructure();

    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  beforeDestroy() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  methods: {
    localizationChanged(localizationId) {
      this.getSurveyStructure();
    },

    // Get survey structure data for the structure treeselect
    getSurveyStructure() {
      SurveysService.getSurveyStructure()
        .then(response => {
          this.structureItems = response.data;
        })
    },

    // Get survey structure data for the structure treeselect
    getSurveyWaves() {
      if (!this.noSurveySelected)
      {
        SurveysService.getSurveyWaves(this.filter)
          .then(response => {
            this.waveItems = response.data;
          })
      };
    },

    // Get survey structure data for the structure treeselect
    getSurveyProperties() {
      if (!this.noSurveySelected)
      {
        SurveysService.getSurveyCustomProperties(this.filter, true)
          .then(response => {
            this.propertyItems = response.data;
          })

        SurveysService.getSurveyCustomProperties(this.filter)
          .then(response => {
            let properties = response.data;
            if (properties && properties.length > 0)
              properties[0].children.forEach(p => p.id = `Property^${p.id}` );
            this.propertySubtotalItems = this.defaultSubtotalItems.concat(properties);
          })
      };
    },

    // Get survey structure data for the scenario treeselect
    getSurveyScenarios() {
      if (!this.noSurveySelected)
      {
        SurveysService.getSurveyScenarios(this.filter)
          .then(response => {
            this.scenarioItems =  response.data;
          });
      }
    },

    // Called whenever the input of the structure treeselect changes
    surveyFilterInput() {
      this.waveValue = [];
      this.propertyValue = [];
      this.propertySubtotalValue = null;
      this.getSurveyWaves();
      this.getSurveyProperties();
      this.getSurveyScenarios();
    },

    downloadFrequencies() {
      this.isExporting = true;
      this.noDataAlert = 0;

      ReportsService.getFrequencies(this.filter, this.options)
        .then(response => {
          this.isExporting = false;

          if (response.status === 204)
            this.noDataAlert = 5;
          else
            this.downloadFile(response);
        })
        .catch(_ => { this.isExporting = false; } );
    }
  }
};
</script>
